// Archivo: MembersTable.jsx

import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Link,
  Tooltip,
  IconButton,
  Chip,
  Stack,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import MergeIcon from "@mui/icons-material/Merge";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { ROUTES } from "constants/routes";
import { useIntl } from "react-intl";
import { withStyles } from "@mui/styles";

const styles = (theme) => ({
  tableContainer: {
    overflowX: "auto",
    overflowY: "auto",
    mb: 2,
  },
  tableHeaderCell: {
    backgroundColor: theme.palette.background.paper,
    fontWeight: 600,
  },
  memberRow: {
    backgroundColor: theme.palette.background.base,
  },
});

const MembersTable = ({
  members,
  selectedOrganization,
  onEditClick = (memberID) => console.log(`Member ${memberID} edit button pressed`),
  onOpenMembership = () => {},
  onStatusClick = (member) => console.log(`Member ${member.id} status button pressed`),
  onMergeClick = (member) => console.log(`Member ${member.id} merge button pressed`),
  onDeleteClick = (member) => console.log(`Member ${member.id} delete button pressed`),
  classes,
}) => {
  const intl = useIntl();
  const [actionsMenu, setActionsMenu] = useState({ anchorEl: null, member: null });

  const closeActionsMenu = () => setActionsMenu({ anchorEl: null, member: null });

  const runMenuAction = (callback) => {
    const member = actionsMenu.member;
    closeActionsMenu();
    if (member) callback(member);
  };

  return (
    <>
      <TableContainer className={classes.tableContainer}>
        <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHeaderCell}>{intl.formatMessage({ id: "csv.name" })}</TableCell>
            <TableCell className={classes.tableHeaderCell}>{intl.formatMessage({ id: "csv.cellphone" })}</TableCell>
            <TableCell className={classes.tableHeaderCell}>{intl.formatMessage({ id: "csv.memberNumber" })}</TableCell>
            <TableCell className={classes.tableHeaderCell}>{intl.formatMessage({ id: "csv.document" })}</TableCell>
            <TableCell className={classes.tableHeaderCell}>{intl.formatMessage({ id: "Categoría oficial" })}</TableCell>
            {selectedOrganization.generates_member_qr_codes && (
              <TableCell className={classes.tableHeaderCell}>QR</TableCell>
            )}
            <TableCell className={classes.tableHeaderCell}>{intl.formatMessage({ id: "Estado" })}</TableCell>
            <TableCell className={classes.tableHeaderCell}>{intl.formatMessage({ id: "Acciones" })}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {members.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                {intl.formatMessage({ id: "No se encontraron resultados" })}
              </TableCell>
            </TableRow>
          ) : (
            members.map((member) => (
              <TableRow key={member.id} hover className={classes.memberRow}>
                <TableCell size="small">
                  <Button
                    color="text"
                    variant="text"
                    onClick={() =>
                      window.open(
                        `${ROUTES.PUBLIC.ORGANIZATION}/${selectedOrganization.slug}/members/${member.id}`,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                  >
                    {member.name}
                    {member.user_id && <CheckCircleIcon color="success" sx={{ ml: 1 }} />}
                  </Button>
                </TableCell>
                <TableCell>{member.cellphone_number}</TableCell>
                <TableCell>{member.member_number}</TableCell>
                <TableCell>{member.document_number}</TableCell>
                <TableCell>{(member.current_official_category_assignments || []).map((assignment) => assignment.category?.name).join(", ") || intl.formatMessage({ id: "Sin categoría" })}</TableCell>
                {selectedOrganization.generates_member_qr_codes && (
                  <TableCell>
                    {member.qr_image_url ? (
                      <Link href={member.qr_image_url} underline="always">
                        Ver QR
                      </Link>
                    ) : (
                      intl.formatMessage({ id: "No disponible" })
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <Chip
                    size="small"
                    variant="filled"
                    color={member.is_active ? "success" : "warning"}
                    label={intl.formatMessage({ id: member.is_active ? "Activo" : "No activo" })}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Button size="small" variant="outlined" onClick={() => onOpenMembership(member)}>
                      Membresía
                    </Button>
                    <Tooltip title={intl.formatMessage({ id: "Editar" })}>
                      <IconButton size="small" onClick={() => onEditClick(member.id)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={intl.formatMessage({ id: "members.lifecycle.moreActions" })}>
                      <IconButton
                        size="small"
                        onClick={(event) => setActionsMenu({ anchorEl: event.currentTarget, member })}
                        aria-label={intl.formatMessage(
                          { id: "members.lifecycle.moreActionsFor" },
                          { name: member.name },
                        )}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
        </Table>
      </TableContainer>
      <Menu anchorEl={actionsMenu.anchorEl} open={Boolean(actionsMenu.anchorEl)} onClose={closeActionsMenu}>
        <MenuItem onClick={() => runMenuAction(onStatusClick)}>
          <ListItemIcon>
            {actionsMenu.member?.is_active ? <PersonOffIcon fontSize="small" /> : <PersonAddIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>
            {intl.formatMessage({
              id: actionsMenu.member?.is_active
                ? "members.lifecycle.deactivateMember"
                : "members.lifecycle.reactivateMember",
            })}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => runMenuAction(onMergeClick)}>
          <ListItemIcon>
            <MergeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{intl.formatMessage({ id: "members.lifecycle.mergeDuplicate" })}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => runMenuAction(onDeleteClick)} sx={{ color: "error.main" }}>
          <ListItemIcon sx={{ color: "error.main" }}>
            <DeleteForeverIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{intl.formatMessage({ id: "members.lifecycle.deletePermanently" })}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default withStyles(styles)(MembersTable);
